import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button, Card, Input, PageHeader } from "@/components/ui";

interface SystemSettings {
  proxy_price: number;
  proxy_currency: string;
  test_duration_hours: number;
  paid_duration_days: number;
  renewal_reminder_days: number;
  referral_default_percent: number;
  mtproxy_ad_tag: string;
  premium_visible_to_users: boolean;
  vpn_visible_to_users: boolean;
}

export default function Settings() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<"proxy" | "vpn" | "plans" | "system">("proxy");
  const current = useQuery<SystemSettings>({
    queryKey: ["settings", "system"],
    queryFn: () => api<SystemSettings>("/api/admin/settings/system"),
  });

  const tabs = [
    ["proxy", "🚀 Тарифы прокси"],
    ["vpn", "🛡 VPN"],
    ["plans", "📦 Планы"],
    ["system", "⚙️ Система"],
  ] as const;

  return (
    <div className="p-4 md:p-8">
      <PageHeader title="Настройки" />
      <div className="inline-flex items-center gap-1 mb-5 p-1 rounded-xl bg-white/[0.03] border border-white/[0.06] flex-wrap">
        {tabs.map(([k, label]) => (
          <button
            key={k}
            onClick={() => setTab(k)}
            className={`h-9 px-4 rounded-lg text-sm font-medium transition-all ${
              tab === k ? "bg-brand-soft text-brand glow-ring" : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "proxy" && <TariffsCard />}
      {tab === "vpn" && <VpnPricesCard />}
      {tab === "plans" && <PlansCard />}
      {tab === "system" && (
        <>
          {current.isLoading && <div className="text-zinc-400">Загрузка…</div>}
          {current.data && (
            <SettingsForm
              initial={current.data}
              onSaved={() => qc.invalidateQueries({ queryKey: ["settings", "system"] })}
            />
          )}
        </>
      )}
    </div>
  );
}

interface PlanSettings {
  test_duration_hours: number;
  paid_duration_days: number;
  renewal_reminder_days: number;
  device_limit_trial: number;
  device_limit_standard: number;
  device_limit_premium: number;
  trial_host_policy: string;
}

function PlansCard() {
  const qc = useQueryClient();
  const q = useQuery<PlanSettings>({
    queryKey: ["settings", "plans"],
    queryFn: () => api<PlanSettings>("/api/admin/settings/plans"),
  });

  const [form, setForm] = useState<Record<string, string>>({});
  useEffect(() => {
    if (q.data) {
      setForm({
        test_duration_hours: String(q.data.test_duration_hours),
        paid_duration_days: String(q.data.paid_duration_days),
        renewal_reminder_days: String(q.data.renewal_reminder_days),
        device_limit_trial: String(q.data.device_limit_trial),
        device_limit_standard: String(q.data.device_limit_standard),
        device_limit_premium: String(q.data.device_limit_premium),
        trial_host_policy: q.data.trial_host_policy,
      });
    }
  }, [q.data]);

  const upd = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: () =>
      api<PlanSettings>("/api/admin/settings/plans", {
        method: "PUT",
        body: JSON.stringify({
          test_duration_hours: Number(form.test_duration_hours),
          paid_duration_days: Number(form.paid_duration_days),
          renewal_reminder_days: Number(form.renewal_reminder_days),
          device_limit_trial: Number(form.device_limit_trial),
          device_limit_standard: Number(form.device_limit_standard),
          device_limit_premium: Number(form.device_limit_premium),
          trial_host_policy: form.trial_host_policy,
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "plans"] }),
  });

  if (q.isLoading || !q.data) return <div className="text-zinc-400">Загрузка…</div>;

  const dirty = Object.keys(form).some(
    (k) => form[k] !== String((q.data as unknown as Record<string, unknown>)[k]),
  );
  const trialOnly = form.trial_host_policy === "trial_only";

  return (
    <div className="max-w-5xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5 mb-5 items-stretch">
        <Card title="Триал (тестовый доступ)" className="h-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Длительность, часов" help="Сколько длится тестовый доступ. По умолчанию 48.">
              <Input value={form.test_duration_hours ?? ""} onChange={(e) => upd("test_duration_hours", e.target.value)} />
            </Field>
            <Field label="Лимит устройств" help="Сколько устройств у триал-подписки.">
              <Input value={form.device_limit_trial ?? ""} onChange={(e) => upd("device_limit_trial", e.target.value)} />
            </Field>
          </div>
          <div className="mt-5">
            <label className="block text-xs text-zinc-500 mb-1.5">Куда сажать триал</label>
            <div className="inline-flex items-center gap-1 p-1 rounded-xl bg-white/[0.03] border border-white/[0.06]">
              {(
                [
                  ["fallback_paid", "Можно на paid-хост"],
                  ["trial_only", "Только на trial-хосты"],
                ] as const
              ).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => upd("trial_host_policy", v)}
                  className={`h-8 px-3 rounded-lg text-xs font-medium transition-all ${
                    form.trial_host_policy === v ? "bg-brand-soft text-brand glow-ring" : "text-zinc-400 hover:text-zinc-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <p className="text-xs text-zinc-600 mt-2 leading-relaxed">
              {trialOnly
                ? "Триалы пойдут только на хосты с kind=trial. Если таких нет — выдача триала не пройдёт (создайте trial-хост в «Хостах»)."
                : "Если trial-хоста нет, триал сядет на paid-хост (текущее поведение — поэтому триалы видны на pd1/pd2)."}
            </p>
          </div>
        </Card>

        <Card title="Платные планы" className="h-full">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Лимит устройств · standard" help="Устройств у обычной платной подписки.">
              <Input value={form.device_limit_standard ?? ""} onChange={(e) => upd("device_limit_standard", e.target.value)} />
            </Field>
            <Field label="Лимит устройств · premium" help="Устройств у premium-подписки.">
              <Input value={form.device_limit_premium ?? ""} onChange={(e) => upd("device_limit_premium", e.target.value)} />
            </Field>
            <Field label="Длительность по умолчанию, дней" help="Для системных/legacy-выдач. Обычные покупки идут по выбранному периоду.">
              <Input value={form.paid_duration_days ?? ""} onChange={(e) => upd("paid_duration_days", e.target.value)} />
            </Field>
            <Field label="Напоминание о продлении, дней" help="За сколько дней до конца слать напоминание.">
              <Input value={form.renewal_reminder_days ?? ""} onChange={(e) => upd("renewal_reminder_days", e.target.value)} />
            </Field>
          </div>
          <p className="text-xs text-zinc-600 mt-4 leading-relaxed">
            Цены и периоды (RUB / Stars) — во вкладке <b className="text-zinc-400">«🚀 Тарифы прокси»</b>.
          </p>
        </Card>
      </div>

      <div className="flex items-center gap-3">
        <Button variant="primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Сохраняю…" : "Сохранить"}
        </Button>
        {save.isSuccess && !dirty && <span className="text-sm text-emerald-400">Сохранено ✓</span>}
        {save.isError && <span className="text-sm text-red-400">{(save.error as Error).message}</span>}
      </div>
    </div>
  );
}

function PriceCell({
  value,
  onChange,
  suffix,
}: {
  value: number;
  onChange: (v: string) => void;
  suffix: string;
}) {
  return (
    <div className="relative">
      <input
        type="number"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-9 pl-3 pr-8 rounded-lg bg-black/25 border border-white/[0.08] text-sm text-zinc-100 text-right tabular-nums focus:outline-none focus:border-brand/60 focus:ring-2 focus:ring-brand/20 transition"
      />
      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-zinc-600 pointer-events-none">
        {suffix}
      </span>
    </div>
  );
}

type Period = "day" | "month" | "3month" | "6month" | "12month";

const PERIOD_LABELS: Record<Period, string> = {
  day: "1 день",
  month: "1 месяц",
  "3month": "3 месяца",
  "6month": "6 месяцев",
  "12month": "12 месяцев",
};

interface TariffsResponse {
  standard: Record<Period, number>;
  premium: Record<Period, number>;
  standard_stars: Record<Period, number>;
  premium_stars: Record<Period, number>;
}

function TariffsCard() {
  const qc = useQueryClient();
  const q = useQuery<TariffsResponse>({
    queryKey: ["settings", "tariffs"],
    queryFn: () => api<TariffsResponse>("/api/admin/settings/tariffs"),
  });
  const [form, setForm] = useState<TariffsResponse | null>(null);

  useEffect(() => {
    if (q.data) {
      setForm({
        standard: { ...q.data.standard },
        premium: { ...q.data.premium },
        standard_stars: { ...q.data.standard_stars },
        premium_stars: { ...q.data.premium_stars },
      });
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: () =>
      api<TariffsResponse>("/api/admin/settings/tariffs", {
        method: "PUT",
        body: JSON.stringify(form),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "tariffs"] }),
  });

  if (q.isLoading || !form) {
    return (
      <Card title="Тарифы">
        <div className="text-zinc-500 text-sm">Загрузка…</div>
      </Card>
    );
  }

  const dirty =
    !!q.data &&
    (
      ["standard", "premium", "standard_stars", "premium_stars"] as (
        | "standard"
        | "premium"
        | "standard_stars"
        | "premium_stars"
      )[]
    ).some((key) =>
      (Object.keys(PERIOD_LABELS) as Period[]).some(
        (p) => Number(form[key][p]) !== Number(q.data![key][p]),
      ),
    );

  const update = (
    key: "standard" | "premium" | "standard_stars" | "premium_stars",
    period: Period,
    raw: string,
  ) => {
    const num = Number(raw);
    if (Number.isNaN(num)) return;
    setForm((prev) =>
      prev
        ? {
            ...prev,
            [key]: { ...prev[key], [period]: num },
          }
        : prev,
    );
  };

  const invalid = (
    ["standard", "premium", "standard_stars", "premium_stars"] as const
  ).some((key) =>
    (Object.keys(PERIOD_LABELS) as Period[]).some((p) => form[key][p] <= 0),
  );

  return (
    <Card title="Тарифы">
      <p className="text-xs text-zinc-500 mb-4">
        Цены в рублях (Platega) и в Telegram Stars. Бот подхватит изменения через ~30 секунд.
      </p>
      <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full border-separate border-spacing-y-1.5 min-w-[560px]">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-zinc-500">
              <th className="text-left font-medium pb-1 pl-1 w-28">Период</th>
              <th className="font-medium pb-1 px-1">🚀 Proxy&nbsp;₽</th>
              <th className="font-medium pb-1 px-1">👑 Premium&nbsp;₽</th>
              <th className="font-medium pb-1 px-1">🚀 Proxy&nbsp;⭐</th>
              <th className="font-medium pb-1 px-1">👑 Premium&nbsp;⭐</th>
            </tr>
          </thead>
          <tbody>
            {(Object.keys(PERIOD_LABELS) as Period[]).map((period) => (
              <tr key={period}>
                <td className="text-sm text-zinc-300 pr-3 pl-1 whitespace-nowrap">
                  {PERIOD_LABELS[period]}
                </td>
                <td className="px-1">
                  <PriceCell value={form.standard[period]} suffix="₽" onChange={(v) => update("standard", period, v)} />
                </td>
                <td className="px-1">
                  <PriceCell value={form.premium[period]} suffix="₽" onChange={(v) => update("premium", period, v)} />
                </td>
                <td className="px-1">
                  <PriceCell value={form.standard_stars[period]} suffix="⭐" onChange={(v) => update("standard_stars", period, v)} />
                </td>
                <td className="px-1">
                  <PriceCell value={form.premium_stars[period]} suffix="⭐" onChange={(v) => update("premium_stars", period, v)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <Button
          variant="primary"
          disabled={!dirty || save.isPending || invalid}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Сохраняю…" : "Сохранить тарифы"}
        </Button>
        {dirty && (
          <Button
            variant="ghost"
            disabled={save.isPending}
            onClick={() =>
              q.data &&
              setForm({
                standard: { ...q.data.standard },
                premium: { ...q.data.premium },
                standard_stars: { ...q.data.standard_stars },
                premium_stars: { ...q.data.premium_stars },
              })
            }
          >
            Отменить
          </Button>
        )}
        {invalid && (
          <span className="text-xs text-red-400">Все цены должны быть &gt; 0</span>
        )}
        {save.isError && (
          <span className="text-xs text-red-400">{(save.error as Error).message}</span>
        )}
        {save.isSuccess && !dirty && (
          <span className="text-xs text-emerald-400">Сохранено</span>
        )}
      </div>
    </Card>
  );
}

const VPN_DAYS = ["7", "30", "90", "180", "365"];
const VPN_DAY_LABELS: Record<string, string> = {
  "7": "1 неделя",
  "30": "1 месяц",
  "90": "3 месяца",
  "180": "6 месяцев",
  "365": "12 месяцев",
};

interface VpnPricesResponse {
  rub: Record<string, number>;
  stars: Record<string, number>;
  device_rub: number;
  device_stars: number;
}

function cloneVpn(d: VpnPricesResponse): VpnPricesResponse {
  return {
    rub: { ...d.rub },
    stars: { ...d.stars },
    device_rub: d.device_rub,
    device_stars: d.device_stars,
  };
}

function VpnPricesCard() {
  const qc = useQueryClient();
  const q = useQuery<VpnPricesResponse>({
    queryKey: ["settings", "vpn-prices"],
    queryFn: () => api<VpnPricesResponse>("/api/admin/settings/vpn-prices"),
  });
  const [form, setForm] = useState<VpnPricesResponse | null>(null);

  useEffect(() => {
    if (q.data) setForm(cloneVpn(q.data));
  }, [q.data]);

  const save = useMutation({
    mutationFn: () =>
      api<VpnPricesResponse>("/api/admin/settings/vpn-prices", {
        method: "PUT",
        body: JSON.stringify(form),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "vpn-prices"] }),
  });

  if (q.isLoading || !form) {
    return (
      <Card title="VPN — цены">
        <div className="text-zinc-500 text-sm">Загрузка…</div>
      </Card>
    );
  }

  const dirty =
    !!q.data &&
    (VPN_DAYS.some(
      (d) =>
        Number(form.rub[d]) !== Number(q.data!.rub[d]) ||
        Number(form.stars[d]) !== Number(q.data!.stars[d]),
    ) ||
      Number(form.device_rub) !== Number(q.data.device_rub) ||
      Number(form.device_stars) !== Number(q.data.device_stars));

  const invalid =
    VPN_DAYS.some((d) => form.rub[d] <= 0 || form.stars[d] <= 0) ||
    form.device_rub <= 0 ||
    form.device_stars <= 0;

  const updDay = (key: "rub" | "stars", d: string, raw: string) => {
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    setForm((prev) => (prev ? { ...prev, [key]: { ...prev[key], [d]: n } } : prev));
  };

  const updDevice = (key: "device_rub" | "device_stars", raw: string) => {
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    setForm((prev) => (prev ? { ...prev, [key]: n } : prev));
  };

  return (
    <Card title="VPN — цены">
      <p className="text-xs text-zinc-500 mb-4">
        Цены на VPN-подписку (Remnawave) и докупку устройства. Бот подхватит изменения
        через ~30 секунд.
      </p>
      <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full border-separate border-spacing-y-1.5 min-w-[420px] max-w-lg">
          <thead>
            <tr className="text-[11px] uppercase tracking-wider text-zinc-500">
              <th className="text-left font-medium pb-1 pl-1 w-36">Период</th>
              <th className="font-medium pb-1 px-1">₽</th>
              <th className="font-medium pb-1 px-1">⭐</th>
            </tr>
          </thead>
          <tbody>
            {VPN_DAYS.map((d) => (
              <tr key={d}>
                <td className="text-sm text-zinc-300 pr-3 pl-1 whitespace-nowrap">
                  {VPN_DAY_LABELS[d]}
                </td>
                <td className="px-1">
                  <PriceCell value={form.rub[d]} suffix="₽" onChange={(v) => updDay("rub", d, v)} />
                </td>
                <td className="px-1">
                  <PriceCell value={form.stars[d]} suffix="⭐" onChange={(v) => updDay("stars", d, v)} />
                </td>
              </tr>
            ))}
            <tr>
              <td className="text-sm text-brand pr-3 pl-1 whitespace-nowrap pt-2">
                +1 устройство
              </td>
              <td className="px-1 pt-2">
                <PriceCell value={form.device_rub} suffix="₽" onChange={(v) => updDevice("device_rub", v)} />
              </td>
              <td className="px-1 pt-2">
                <PriceCell value={form.device_stars} suffix="⭐" onChange={(v) => updDevice("device_stars", v)} />
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <Button
          variant="primary"
          disabled={!dirty || save.isPending || invalid}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Сохраняю…" : "Сохранить VPN-цены"}
        </Button>
        {dirty && (
          <Button
            variant="ghost"
            disabled={save.isPending}
            onClick={() => q.data && setForm(cloneVpn(q.data))}
          >
            Отменить
          </Button>
        )}
        {invalid && (
          <span className="text-xs text-red-400">Все цены должны быть &gt; 0</span>
        )}
        {save.isError && (
          <span className="text-xs text-red-400">{(save.error as Error).message}</span>
        )}
        {save.isSuccess && !dirty && (
          <span className="text-xs text-emerald-400">Сохранено</span>
        )}
      </div>
    </Card>
  );
}

interface FormState {
  proxy_price: string;
  test_duration_hours: string;
  paid_duration_days: string;
  renewal_reminder_days: string;
  referral_default_percent: string;
  mtproxy_ad_tag: string;
  premium_visible_to_users: string;
  vpn_visible_to_users: string;
}

function fromData(s: SystemSettings): FormState {
  return {
    proxy_price: String(s.proxy_price),
    test_duration_hours: String(s.test_duration_hours),
    paid_duration_days: String(s.paid_duration_days),
    renewal_reminder_days: String(s.renewal_reminder_days),
    referral_default_percent: String(s.referral_default_percent),
    mtproxy_ad_tag: s.mtproxy_ad_tag ?? "",
    premium_visible_to_users: s.premium_visible_to_users ? "1" : "0",
    vpn_visible_to_users: s.vpn_visible_to_users ? "1" : "0",
  };
}

function SettingsForm({
  initial,
  onSaved,
}: {
  initial: SystemSettings;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<FormState>(fromData(initial));

  useEffect(() => {
    setForm(fromData(initial));
  }, [initial]);

  const upd = <K extends keyof FormState>(k: K, v: string) =>
    setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: () =>
      api<SystemSettings>("/api/admin/settings/system", {
        method: "PUT",
        body: JSON.stringify({
          proxy_price: Number(form.proxy_price),
          test_duration_hours: Number(form.test_duration_hours),
          paid_duration_days: Number(form.paid_duration_days),
          renewal_reminder_days: Number(form.renewal_reminder_days),
          referral_default_percent: Number(form.referral_default_percent),
          mtproxy_ad_tag: form.mtproxy_ad_tag.trim().toLowerCase(),
          premium_visible_to_users: form.premium_visible_to_users === "1",
          vpn_visible_to_users: form.vpn_visible_to_users === "1",
        }),
      }),
    onSuccess: () => onSaved(),
  });

  const tag = form.mtproxy_ad_tag.trim().toLowerCase();
  const adTagInvalid =
    tag.length > 0 && (tag.length !== 32 || !/^[0-9a-f]+$/.test(tag));

  const current = fromData(initial);
  const dirty = (Object.keys(form) as (keyof FormState)[]).some(
    (k) => form[k].trim() !== current[k],
  );

  return (
    <div className="max-w-5xl">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5 mb-5 items-stretch">
      <Card title="Продукт" className="h-full">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Цена подписки, ₽" help="Сумма за одну покупку paid-доступа.">
            <Input
              value={form.proxy_price}
              onChange={(e) => upd("proxy_price", e.target.value)}
            />
          </Field>
          <Field
            label="Длительность теста, часов"
            help="Сколько живёт бесплатный test-доступ. По умолчанию 48."
          >
            <Input
              value={form.test_duration_hours}
              onChange={(e) => upd("test_duration_hours", e.target.value)}
            />
          </Field>
          <Field
            label="Длительность paid, дней"
            help="Сколько живёт оплаченный доступ за одну покупку. По умолчанию 30."
          >
            <Input
              value={form.paid_duration_days}
              onChange={(e) => upd("paid_duration_days", e.target.value)}
            />
          </Field>
        </div>
      </Card>

      <Card title="Напоминания и реф" className="h-full">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Напомнить о продлении за N дней"
            help="За сколько дней до истечения paid бот шлёт «Подписка скоро истечёт». 0 = не напоминать."
          >
            <Input
              value={form.renewal_reminder_days}
              onChange={(e) => upd("renewal_reminder_days", e.target.value)}
            />
          </Field>
          <Field
            label="Реф-процент по умолчанию, %"
            help="Сколько начисляется рефереру с каждой покупки, если на юзера не установлен персональный процент."
          >
            <Input
              value={form.referral_default_percent}
              onChange={(e) => upd("referral_default_percent", e.target.value)}
            />
          </Field>
        </div>
      </Card>

      <Card title="Видимость в боте" className="h-full">
        <div className="space-y-4">
          <Field
            label="Premium-тариф юзерам"
            help="При выключенном — в боте видна только «NoLags Proxy». Инфраструктура/БД/активные premium-доступы не трогаются."
          >
            <Toggle
              checked={form.premium_visible_to_users === "1"}
              onChange={(v) => upd("premium_visible_to_users", v ? "1" : "0")}
            />
          </Field>
          <Field
            label="VPN юзерам"
            help="При выключенном — кнопка «VPN» скрыта, callback'и заблокированы. Remnawave не трогается. Включай на раскатке."
          >
            <Toggle
              checked={form.vpn_visible_to_users === "1"}
              onChange={(v) => upd("vpn_visible_to_users", v ? "1" : "0")}
            />
          </Field>
        </div>
      </Card>

      <Card title="MTProxy ad_tag" className="h-full">
        <Field
          label="Глобальный ad_tag"
          help="Тег от @MTProxyAdminBot (32 hex). Применяется ко всем серверам и кластерам, у которых свой ad_tag не задан. Пусто = без тега."
        >
          <Input
            value={form.mtproxy_ad_tag}
            placeholder="пусто = без тега"
            onChange={(e) => upd("mtproxy_ad_tag", e.target.value)}
            className="font-mono"
          />
          {adTagInvalid && (
            <p className="text-xs text-red-400 mt-1.5">
              Должно быть ровно 32 hex-символа или пусто.
            </p>
          )}
        </Field>
      </Card>

      <CloudflareAccountsCard />
      </div>

      <div className="flex items-center gap-3">
        <Button
          variant="primary"
          disabled={!dirty || save.isPending || adTagInvalid}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Сохраняю…" : "Сохранить"}
        </Button>
        {dirty && (
          <Button
            variant="ghost"
            disabled={save.isPending}
            onClick={() => setForm(fromData(initial))}
          >
            Отменить
          </Button>
        )}
        {save.isError && (
          <span className="text-xs text-red-400">{(save.error as Error).message}</span>
        )}
        {save.isSuccess && !dirty && (
          <span className="text-xs text-emerald-400">Сохранено. Бот подхватит через ~30 сек.</span>
        )}
      </div>
    </div>
  );
}

interface CfAccount {
  zone_id: string;
  name?: string | null;
  token_set?: boolean;
  primary?: boolean;
  token?: string;
}

function CloudflareAccountsCard() {
  const qc = useQueryClient();
  const q = useQuery<{ accounts: CfAccount[] }>({
    queryKey: ["cloudflare", "accounts"],
    queryFn: () => api("/api/admin/settings/cloudflare/accounts"),
  });
  const [rows, setRows] = useState<CfAccount[]>([]);
  const [showHelp, setShowHelp] = useState(false);
  useEffect(() => {
    if (q.data) setRows(q.data.accounts.map((a) => ({ ...a, token: "" })));
  }, [q.data]);

  const save = useMutation<{ ok: boolean; accounts: { zone_id: string; ok: boolean; zone_name?: string; error?: string }[] }>({
    mutationFn: () =>
      api("/api/admin/settings/cloudflare/accounts", {
        method: "PUT",
        body: JSON.stringify({
          zones: rows
            .filter((r) => r.zone_id.trim())
            .map((r) => ({ zone_id: r.zone_id.trim(), token: r.token || null, name: r.name || null })),
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cloudflare", "accounts"] });
      qc.invalidateQueries({ queryKey: ["cloudflare", "status"] });
      qc.invalidateQueries({ queryKey: ["domains"] });
    },
  });

  const set = (i: number, k: keyof CfAccount, v: string) =>
    setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));

  return (
    <Card title="Cloudflare — аккаунты / зоны (автопривязка доменов)" className="lg:col-span-2">
      <p className="text-xs text-zinc-500 mb-2 leading-relaxed">
        Несколько CF-аккаунтов/зон. У <b className="text-zinc-400">каждой зоны — СВОЙ токен</b>
        {" "}(разные аккаунты = разные токены, не только Zone ID). Хост с доменом привяжется
        автоматически в ту зону, чьё имя совпадает по суффиксу
        (<b className="text-zinc-400">rekrak.nolags.pw → nolags.pw</b>). Первая — основная.
        Токен — с правами <b className="text-zinc-400">Zone · DNS · Edit</b> в том аккаунте, где зона.
      </p>
      <button
        type="button"
        onClick={() => setShowHelp((v) => !v)}
        className="text-xs text-sky-400/90 hover:text-sky-300 mb-3 inline-flex items-center gap-1"
      >
        <span>📖 Инструкция: где взять Zone ID и API-токен</span>
        <span className="text-zinc-500">{showHelp ? "▲" : "▼"}</span>
      </button>
      {showHelp && (
        <div className="rounded-xl border border-sky-400/15 bg-sky-400/[0.04] p-4 mb-4 text-xs text-zinc-400 leading-relaxed space-y-3">
          <div>
            <div className="text-zinc-200 font-medium mb-1">🔑 Zone ID</div>
            <ol className="list-decimal pl-4 space-y-0.5 marker:text-zinc-600">
              <li>Открой <b className="text-zinc-300">dash.cloudflare.com</b> и выбери свой домен (зону).</li>
              <li>Вкладка <b className="text-zinc-300">Overview</b> (Обзор) → правая колонка <b className="text-zinc-300">API</b>.</li>
              <li>Строка <b className="text-zinc-300">Zone ID</b> → <b className="text-zinc-300">Click to copy</b>. Это 32 hex-символа → в поле «Zone ID».</li>
            </ol>
          </div>
          <div>
            <div className="text-zinc-200 font-medium mb-1">🎫 API-токен (Zone · DNS · Edit)</div>
            <ol className="list-decimal pl-4 space-y-0.5 marker:text-zinc-600">
              <li>Профиль (правый верх) → <b className="text-zinc-300">My Profile → API Tokens</b> (или <span className="font-mono text-zinc-300">dash.cloudflare.com/profile/api-tokens</span>).</li>
              <li><b className="text-zinc-300">Create Token</b> → шаблон <b className="text-zinc-300">Edit zone DNS</b> → <b className="text-zinc-300">Use template</b>.</li>
              <li>Permissions уже: <b className="text-zinc-300">Zone · DNS · Edit</b> — не трогай.</li>
              <li>Zone Resources: <b className="text-zinc-300">Include · Specific zone · твой домен</b> (или All zones этого аккаунта).</li>
              <li><b className="text-zinc-300">Continue to summary → Create Token</b> → скопируй токен (показывается <b className="text-amber-300/90">один раз!</b>) → в поле «API-токен».</li>
            </ol>
          </div>
          <div className="text-zinc-500 border-t border-white/[0.06] pt-2">
            ⚠️ Zone ID и токен должны быть из <b className="text-zinc-400">одного аккаунта</b>. Токен из другого
            аккаунта к чужой зоне не подойдёт — будет ошибка проверки.
          </div>
        </div>
      )}
      <div className="space-y-3">
        {rows.map((r, i) => (
          <div key={i} className="rounded-xl border border-white/[0.08] bg-white/[0.02] p-3.5">
            <div className="flex items-center justify-between mb-2.5">
              <span className="text-xs font-medium text-zinc-300">
                {i === 0 ? "🌐 Основная зона" : `🌐 Зона ${i + 1}`}
                {r.name ? <span className="text-zinc-500 font-normal"> · {r.name}</span> : null}
              </span>
              <button
                onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))}
                className="text-xs text-zinc-500 hover:text-red-400"
                title="удалить зону"
              >
                ✕ удалить
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                  Zone ID
                </label>
                <Input
                  value={r.zone_id}
                  placeholder="32 hex (Overview → API)"
                  onChange={(e) => set(i, "zone_id", e.target.value)}
                  className="font-mono w-full"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">
                  API-токен этого аккаунта · Zone·DNS·Edit
                </label>
                <Input
                  type="password"
                  value={r.token ?? ""}
                  placeholder={r.token_set ? "•••••• сохранён (пусто = не менять)" : "токен CF-аккаунта"}
                  onChange={(e) => set(i, "token", e.target.value)}
                  className="font-mono w-full"
                  autoComplete="off"
                />
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="text-xs text-zinc-600">Пока нет CF-аккаунтов. Добавь первый ↓</div>
        )}
      </div>
      <div className="flex items-center gap-3 mt-4 flex-wrap">
        <Button
          variant="ghost"
          onClick={() => setRows((rs) => [...rs, { zone_id: "", token: "" }])}
        >
          + Аккаунт / зона
        </Button>
        <Button variant="primary" disabled={save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? "Сохраняю…" : "Сохранить и проверить"}
        </Button>
        {save.isSuccess && (
          <span className="text-xs">
            {save.data?.accounts?.map((z, i) => (
              <span key={i} className={z.ok ? "text-emerald-400" : "text-red-400"}>
                {i > 0 ? ", " : ""}
                {z.ok ? `✓ ${z.zone_name || z.zone_id}` : `⛔ ${z.zone_id}${z.error ? ` (${z.error})` : ""}`}
              </span>
            ))}
          </span>
        )}
        {save.isError && (
          <span className="text-xs text-red-400">{(save.error as Error).message}</span>
        )}
      </div>
    </Card>
  );
}

function Field({
  label,
  help,
  children,
}: {
  label: string;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col">
      {/* min-h под 2 строки + leading-tight — 1- и 2-строчные лейблы держат инпуты
          на ОДНОЙ высоте в ряду (было: «Цена» выше, «Длительность» ниже). */}
      <label className="block text-xs text-zinc-500 mb-1.5 leading-tight min-h-[2.1em]">
        {label}
      </label>
      {children}
      {help && <p className="text-xs text-zinc-600 mt-1.5 leading-snug">{help}</p>}
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  onLabel = "Видно юзерам",
  offLabel = "Скрыто",
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  onLabel?: string;
  offLabel?: string;
}) {
  return (
    <div className="flex items-center gap-3 mt-1">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${
          checked ? "bg-brand shadow-glow" : "bg-zinc-700"
        }`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
            checked ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
      <span className={`text-sm ${checked ? "text-brand" : "text-zinc-400"}`}>
        {checked ? onLabel : offLabel}
      </span>
    </div>
  );
}
